/**
 * Object manipulation utilities.
 */

export function removeProperties<ObjectType, PropsArrayType extends string[]>(
  obj: ObjectType,
  props: PropsArrayType,
): ObjectType {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((o) => removeProperties(o, props)) as ObjectType;
  }

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([key]) => !props.includes(key))
      .map(([k, v]) => [k, removeProperties(v, props)]),
  ) as ObjectType;
}

/**
 * Gets a value from an object using dot notation path.
 *
 * Resolves flat-first: if the path is literally a top-level key on the
 * object, that value is returned directly. Otherwise, the path is split
 * on `.` and walked as a nested property access chain.
 *
 * This lets the same helper resolve both nested object access
 * (`{ string: { externalId: 'abc' } }` + `"string.externalId"`) and flat
 * columns whose names happen to contain dots, colons, dashes, spaces, or
 * any other character that a path walker would treat as a delimiter or
 * invalid identifier — e.g. Cognite ref columns materialized as flat
 * `"string.externalId"` keys by `applySelect`.
 *
 * @example
 * getNestedValue({ string: { externalId: 'abc' } }, 'string.externalId') // 'abc'  (nested walk)
 * getNestedValue({ 'string.externalId': 'abc' }, 'string.externalId') // 'abc'  (flat-first)
 * getNestedValue({ name: 'test' }, 'name') // 'test'
 * getNestedValue({ foo: null }, 'foo.bar') // undefined
 */
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  // Flat-first: if the path is literally a key on the object, return that
  // value directly. Handles flat columns with dots/colons/dashes/spaces in
  // their names (e.g. `string.externalId`, `metric:volume`, `kebab-case`),
  // which path-walking would otherwise miss.
  if (Object.hasOwn(obj, path)) {
    return obj[path];
  }

  // Fallback: walk the dot-separated path for nested object access.
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Checks if a dot notation path exists and has a defined value in the object.
 */
export function hasNestedPath(obj: Record<string, unknown>, path: string): boolean {
  return getNestedValue(obj, path) !== undefined;
}
